import { ref, Ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import { parseHealth, parseReady } from './aiAgentContract'

/**
 * useAgentHealth — reachability probe for the ai-agent-service.
 *
 * Drives the assistant config page's reachability badge. `check()` hits the
 * unauthenticated GET `/health`; `ready()` hits GET `/ready` (which also reports
 * whether the SQLite store opened). Both collapse any transport/parse failure
 * into a single 'unreachable' state — the badge is a coarse up/down signal, not
 * an error surface, so failures are swallowed (logged, never re-thrown, never a
 * global toast).
 *
 * Network access lives ONLY here (never in the .vue), honoring the
 * `no_httpservice_in_vue` qa rule and CLAUDE.md rules 3/5. Absolute URL to
 * `config.api.agentHost`; `/health` and `/ready` are unauthenticated so no JWT
 * is required (httpService still attaches one if present — harmless).
 */
export function useAgentHealth() {
  const status: Ref<'ok' | 'unreachable' | null> = ref(null)
  const ready: Ref<'ready' | 'not_ready' | 'unreachable' | null> = ref(null)
  const checking: Ref<boolean> = ref(false)

  async function check(): Promise<'ok' | 'unreachable'> {
    checking.value = true
    try {
      const raw: unknown = await httpService.get(`${config.api.agentHost}/health`, {
        skipErrorDisplay: true,
        skipLoading: true,
        logContext: 'useAgentHealth',
      })
      parseHealth(raw)
      status.value = 'ok'
      return 'ok'
    } catch (err) {
      status.value = 'unreachable'
      const message = err instanceof Error ? err.message : String(err)
      log('useAgentHealth', 'check', 'Assistente inacessível', { message })
      return 'unreachable'
    } finally {
      checking.value = false
    }
  }

  async function checkReady(): Promise<'ready' | 'not_ready' | 'unreachable'> {
    checking.value = true
    try {
      const raw: unknown = await httpService.get(`${config.api.agentHost}/ready`, {
        skipErrorDisplay: true,
        skipLoading: true,
        logContext: 'useAgentHealth',
      })
      const parsed = parseReady(raw)
      ready.value = parsed.status
      return parsed.status
    } catch (err) {
      ready.value = 'unreachable'
      const message = err instanceof Error ? err.message : String(err)
      log('useAgentHealth', 'checkReady', 'Prontidão indisponível', { message })
      return 'unreachable'
    } finally {
      checking.value = false
    }
  }

  return {
    status,
    ready,
    checking,
    check,
    checkReady,
  }
}
